import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { setLang } from "@/i18n";
import { translateDeep, activeDict } from "@/lib/translate";
import api, { assetUrl } from "@/lib/api";
import { StoreHeader, StoreFooter, textOn, fontStack, ensureFontsLoaded, collectFonts } from "@/lib/storeBlocks";
import { CircleNotch, ArrowLeft } from "@phosphor-icons/react";

export default function BlogPost() {
  const { slug, postSlug } = useParams();
  const { i18n } = useTranslation();
  const [store, setStore] = useState(null);
  const [site, setSite] = useState({});
  const [post, setPost] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/public/store/${slug}`, { withCredentials: false }).then((r) => { setStore(r.data.store); setSite(r.data.store?.site || {}); }).catch(() => {});
    api.get(`/public/store/${slug}/blog/${postSlug}`, { withCredentials: false }).then((r) => setPost(r.data)).catch(() => setErr("Artículo no encontrado"));
  }, [slug, postSlug]);

  const bg = store?.theme_color || "#0B0B0F";
  const accent = store?.accent || "#C6A15B";
  const primaryLang = (store?.language || "en").toLowerCase();
  const displayLang = (i18n.language || primaryLang).slice(0, 2).toLowerCase();
  const dict = activeDict(store, displayLang);
  const tSite = dict ? translateDeep(site, dict) : site;
  const tPost = dict && post ? translateDeep(post, dict) : post;
  const headingFont = fontStack(site?.font);
  useEffect(() => { ensureFontsLoaded(collectFonts(site)); }, [site]);
  const ctx = { store: store || {}, slug, accent, bg, headingFont, interactive: true, lang: displayLang, primaryLang, onLang: (l) => setLang(l), translate: (o) => (dict ? translateDeep(o, dict) : o) };

  useEffect(() => {
    const col = bg;
    document.documentElement.style.background = col; document.body.style.background = col;
    if (tPost) document.title = (tPost.seo_title || tPost.title) + " — " + (store?.name || "");
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
    if (tPost) m.content = tPost.seo_description || tPost.excerpt || "";
    return () => { document.documentElement.style.background = ""; document.body.style.background = ""; };
  }, [tPost, bg, store]);

  if (err) return <div className="min-h-screen flex items-center justify-center text-neutral-400" style={{ background: bg }} data-testid="blog-post-error">{err} · <Link to={`/tienda/${slug}`} className="underline ml-1">Volver</Link></div>;
  if (!post || !store) return <div className="min-h-screen flex items-center justify-center" style={{ background: bg }}><CircleNotch size={28} className="animate-spin text-neutral-500" /></div>;

  return (
    <div className="min-h-screen overflow-x-hidden storefront-brand" style={{ backgroundColor: bg, color: textOn(bg), fontFamily: headingFont }} data-testid="blog-post-page">
      {tSite.header && tSite.header.enabled !== false && <StoreHeader header={tSite.header} ctx={ctx} />}
      <article className="max-w-3xl mx-auto px-6 py-12">
        <Link to={`/tienda/${slug}`} className="inline-flex items-center gap-1.5 text-sm opacity-70 hover:opacity-100 mb-6"><ArrowLeft size={14} /> Volver a la tienda</Link>
        <h1 className="text-3xl sm:text-5xl font-bold tracking-tight" style={{ fontFamily: headingFont }} data-testid="blog-post-title">{tPost.title}</h1>
        {tPost.excerpt && <p className="text-lg opacity-75 mt-3">{tPost.excerpt}</p>}
        {tPost.cover_url && <img src={assetUrl(tPost.cover_url)} alt={tPost.title} className="w-full rounded-2xl mt-8 object-cover" />}
        <div className="prose-invert max-w-none mt-8 leading-relaxed whitespace-pre-line [&_img]:rounded-xl [&_a]:underline" style={{ color: textOn(bg) }} dangerouslySetInnerHTML={{ __html: tPost.body_html || "" }} />
      </article>
      {tSite.footer && tSite.footer.enabled !== false && <StoreFooter footer={tSite.footer} ctx={ctx} />}
    </div>
  );
}
